import axios from "axios";
import {
  makeAutoObservable,
  action,
  observable,
  computed,
  runInAction,
} from "mobx";
import { get, eq } from "lodash";
import { API_HOST, RequestState } from "../constants";
import { getSessionCookie } from "../utils";
import { userStore } from "../stores";

export class DashboardController {
  // Biomonitoring data
  biomonitoringData = {
    cholesterol: { current: 0, target: 200, unit: "mg/dL", status: "normal" },
    bloodSugar: { current: 0, target: 100, unit: "mg/dL", status: "normal" },
    bloodPressure: {
      systolic: 0,
      diastolic: 0,
      target: "120/80",
      unit: "mmHg",
      status: "normal",
    },
    hemoglobin: { current: 0, target: 14, unit: "g/dL", status: "normal" },
    vitaminD: { current: 0, target: 30, unit: "ng/mL", status: "normal" },
    bmi: { current: 0, target: 25, unit: "kg/m²", status: "normal" },
    lastUpdated: null,
  };

  // Dietary goals
  dietaryGoals = {
    caloriesTarget: 2000,
    caloriesConsumed: 0,
    proteinTarget: 150,
    proteinConsumed: 0,
    carbsTarget: 250,
    carbsConsumed: 0,
    fatTarget: 65,
    fatConsumed: 0,
    fiberTarget: 25,
    fiberConsumed: 0,
    waterTarget: 8,
    waterConsumed: 0,
  };

  // Weekly meal plan
  weeklyMeals = [];

  // Request states
  biomonitoringRequestState = RequestState.IDLE;
  dietaryGoalsRequestState = RequestState.IDLE;
  weeklyMealsRequestState = RequestState.IDLE;

  // Error messages
  biomonitoringMessage = "";
  dietaryGoalsMessage = "";
  weeklyMealsMessage = "";

  _userStore = userStore;

  constructor() {
    makeAutoObservable(this, {
      biomonitoringData: observable,
      dietaryGoals: observable,
      weeklyMeals: observable,
      biomonitoringRequestState: observable,
      dietaryGoalsRequestState: observable,
      weeklyMealsRequestState: observable,
      biomonitoringMessage: observable,
      dietaryGoalsMessage: observable,
      weeklyMealsMessage: observable,
      fetchBiomonitoring: action,
      fetchDietaryGoals: action,
      fetchWeeklyMeals: action,
      updateDietaryProgress: action,
      getBiomonitoringData: computed,
      getDietaryGoals: computed,
      getWeeklyMeals: computed,
    });

    // Initialize dashboard data
    this.initializeDashboard();
  }

  async initializeDashboard() {
    if (this._userStore.isLoggedIn) {
      await Promise.all([
        this.fetchBiomonitoring(),
        this.fetchDietaryGoals(),
        this.fetchWeeklyMeals(),
      ]);
    }
  }

  async fetchBiomonitoring() {
    runInAction(() => {
      this.biomonitoringRequestState = RequestState.REQUESTED;
      this.biomonitoringMessage = "";
    });

    try {
      const cookie = await getSessionCookie();
      const response = await axios.get(`${API_HOST}user/onboarding/status`, {
        headers: {
          Authorization: cookie,
        },
      });

      if (eq(response.status, 200)) {
        const data = get(response, "data", {});
        const labs = get(data, "labs", {}) || {};
        const metrics = get(labs, "metrics", {}) || labs;
        const systolic = Number(get(metrics, "systolic", 0) || get(metrics, "bloodPressureSystolic", 0) || 0);
        const diastolic = Number(get(metrics, "diastolic", 0) || get(metrics, "bloodPressureDiastolic", 0) || 0);
        const cholesterolTotal = Number(get(metrics, "cholesterolTotal", 0));
        const bloodSugar = Number(get(metrics, "a1c", 0));
        runInAction(() => {
          this.biomonitoringData = {
            cholesterol: {
              current: cholesterolTotal,
              target: get(labs, "targets.cholesterolTotal", 200),
              unit: "mg/dL",
              status: this.getHealthStatus(
                cholesterolTotal,
                get(labs, "targets.cholesterolTotal", 200),
                "cholesterol"
              ),
            },
            bloodSugar: {
              current: bloodSugar,
              target: get(labs, "targets.a1c", 100),
              unit: "mg/dL",
              status: this.getHealthStatus(
                bloodSugar,
                get(labs, "targets.a1c", 100),
                "bloodSugar"
              ),
            },
            bloodPressure: {
              systolic,
              diastolic,
              target: "120/80",
              unit: "mmHg",
              status: this.getBloodPressureStatus(
                systolic,
                diastolic
              ),
            },
            hemoglobin: {
              current: get(data, "hemoglobin.current", 0),
              target: get(data, "hemoglobin.target", 14),
              unit: "g/dL",
              status: this.getHealthStatus(
                get(data, "hemoglobin.current", 0),
                get(data, "hemoglobin.target", 14),
                "hemoglobin"
              ),
            },
            vitaminD: {
              current: get(data, "vitaminD.current", 0),
              target: get(data, "vitaminD.target", 30),
              unit: "ng/mL",
              status: this.getHealthStatus(
                get(data, "vitaminD.current", 0),
                get(data, "vitaminD.target", 30),
                "vitaminD"
              ),
            },
            bmi: {
              current: get(data, "bmi.current", 0),
              target: get(data, "bmi.target", 25),
              unit: "kg/m²",
              status: this.getBMIStatus(get(data, "bmi.current", 0)),
            },
            lastUpdated: get(data, "lastUpdated", null),
          };
        });
      }
    } catch (error) {
      console.log("Error fetching biomonitoring data", error);
      // Use sample data for demo purposes
      runInAction(() => {
        this.biomonitoringData = {
          cholesterol: {
            current: 185,
            target: 200,
            unit: "mg/dL",
            status: this.getHealthStatus(185, 200, "cholesterol"),
          },
          bloodSugar: {
            current: 95,
            target: 100,
            unit: "mg/dL",
            status: this.getHealthStatus(95, 100, "bloodSugar"),
          },
          bloodPressure: {
            systolic: 118,
            diastolic: 75,
            target: "120/80",
            unit: "mmHg",
            status: this.getBloodPressureStatus(118, 75),
          },
          hemoglobin: {
            current: 13.8,
            target: 14,
            unit: "g/dL",
            status: this.getHealthStatus(13.8, 14, "hemoglobin"),
          },
          vitaminD: {
            current: 32,
            target: 30,
            unit: "ng/mL",
            status: this.getHealthStatus(32, 30, "vitaminD"),
          },
          bmi: {
            current: 23.2,
            target: 25,
            unit: "kg/m²",
            status: this.getBMIStatus(23.2),
          },
          lastUpdated: "2024-01-15T10:30:00Z",
        };
        this.biomonitoringMessage = "";
      });
    }

    runInAction(() => {
      this.biomonitoringRequestState = RequestState.RECEIVED;
    });
  }

  async fetchDietaryGoals() {
    runInAction(() => {
      this.dietaryGoalsRequestState = RequestState.REQUESTED;
      this.dietaryGoalsMessage = "";
    });

    try {
      const cookie = await getSessionCookie();
      const response = await axios.get(`${API_HOST}dashboard/dietary-goals`, {
        headers: {
          Authorization: cookie,
        },
      });

      if (eq(response.status, 200)) {
        const data = get(response, "data.goals", {});

        runInAction(() => {
          this.dietaryGoals = {
            caloriesTarget: get(data, "caloriesTarget", 2000),
            caloriesConsumed: get(data, "caloriesConsumed", 0),
            proteinTarget: get(data, "proteinTarget", 150),
            proteinConsumed: get(data, "proteinConsumed", 0),
            carbsTarget: get(data, "carbsTarget", 250),
            carbsConsumed: get(data, "carbsConsumed", 0),
            fatTarget: get(data, "fatTarget", 65),
            fatConsumed: get(data, "fatConsumed", 0),
            fiberTarget: get(data, "fiberTarget", 25),
            fiberConsumed: get(data, "fiberConsumed", 0),
            waterTarget: get(data, "waterTarget", 8),
            waterConsumed: get(data, "waterConsumed", 0),
          };
        });
      }
    } catch (error) {
      console.log("Error fetching dietary goals", error);
      // Use sample data for demo purposes
      runInAction(() => {
        this.dietaryGoals = {
          caloriesTarget: 2000,
          caloriesConsumed: 1450,
          proteinTarget: 150,
          proteinConsumed: 95,
          carbsTarget: 250,
          carbsConsumed: 180,
          fatTarget: 65,
          fatConsumed: 42,
          fiberTarget: 25,
          fiberConsumed: 18,
          waterTarget: 8,
          waterConsumed: 6,
        };
        this.dietaryGoalsMessage = "";
      });
    }

    runInAction(() => {
      this.dietaryGoalsRequestState = RequestState.RECEIVED;
    });
  }

  async fetchWeeklyMeals() {
    runInAction(() => {
      this.weeklyMealsRequestState = RequestState.REQUESTED;
      this.weeklyMealsMessage = "";
    });

    try {
      const cookie = await getSessionCookie();
      const response = await axios.get(`${API_HOST}dashboard/weekly-meals`, {
        headers: {
          Authorization: cookie,
        },
      });

      if (eq(response.status, 200)) {
        const meals = get(response, "data.meals", []);

        runInAction(() => {
          this.weeklyMeals = meals;
        });
      }
    } catch (error) {
      console.log("Error fetching weekly meals", error);
      // Use sample data for demo purposes
      runInAction(() => {
        this.weeklyMeals = [
          {
            date: new Date().toISOString(),
            type: "breakfast",
            name: "Overnight Oats with Berries",
            description:
              "Creamy oats topped with fresh blueberries and almonds",
            calories: 350,
            image:
              "https://images.unsplash.com/photo-1571115764595-644a1f56a55c?w=300&h=200&fit=crop",
            tags: ["healthy", "fiber-rich", "antioxidants"],
          },
          {
            date: new Date(Date.now() + 86400000).toISOString(),
            type: "lunch",
            name: "Quinoa Buddha Bowl",
            description:
              "Nutrient-packed bowl with quinoa, roasted vegetables, and tahini dressing",
            calories: 520,
            image:
              "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=300&h=200&fit=crop",
            tags: ["vegan", "protein-rich", "colorful"],
          },
          {
            date: new Date(Date.now() + 172800000).toISOString(),
            type: "dinner",
            name: "Grilled Salmon with Asparagus",
            description: "Wild-caught salmon with roasted asparagus and lemon",
            calories: 450,
            image:
              "https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=300&h=200&fit=crop",
            tags: ["omega-3", "low-carb", "heart-healthy"],
          },
          {
            date: new Date(Date.now() + 259200000).toISOString(),
            type: "breakfast",
            name: "Greek Yogurt Parfait",
            description:
              "Layered Greek yogurt with granola and seasonal fruits",
            calories: 280,
            image:
              "https://images.unsplash.com/photo-1488477304112-4944851de03d?w=300&h=200&fit=crop",
            tags: ["probiotics", "protein", "calcium"],
          },
          {
            date: new Date(Date.now() + 345600000).toISOString(),
            type: "lunch",
            name: "Mediterranean Wrap",
            description:
              "Whole wheat wrap with hummus, vegetables, and feta cheese",
            calories: 380,
            image:
              "https://images.unsplash.com/photo-1565299585323-38174c273ca6?w=300&h=200&fit=crop",
            tags: ["mediterranean", "fiber", "fresh"],
          },
          {
            date: new Date(Date.now() + 432000000).toISOString(),
            type: "dinner",
            name: "Chicken Stir Fry",
            description:
              "Lean chicken breast with mixed vegetables and brown rice",
            calories: 420,
            image:
              "https://images.unsplash.com/photo-1603133872878-684f208fb84b?w=300&h=200&fit=crop",
            tags: ["lean-protein", "vegetables", "balanced"],
          },
        ];
        this.weeklyMealsMessage = "";
      });
    }

    runInAction(() => {
      this.weeklyMealsRequestState = RequestState.RECEIVED;
    });
  }

  updateDietaryProgress(nutrient, amount) {
    runInAction(() => {
      if (this.dietaryGoals[`${nutrient}Consumed`] !== undefined) {
        this.dietaryGoals[`${nutrient}Consumed`] += amount;
      }
    });
  }

  // Helper methods for health status
  getHealthStatus(current, target, type) {
    const ratio = current / target;

    switch (type) {
      case "cholesterol":
        if (current < 200) return "good";
        if (current < 240) return "borderline";
        return "high";

      case "bloodSugar":
        if (current < 100) return "normal";
        if (current < 126) return "prediabetic";
        return "diabetic";

      case "hemoglobin":
        if (ratio >= 0.9 && ratio <= 1.1) return "normal";
        if (ratio < 0.9) return "low";
        return "high";

      case "vitaminD":
        if (current >= 30) return "sufficient";
        if (current >= 20) return "insufficient";
        return "deficient";

      default:
        if (ratio >= 0.9 && ratio <= 1.1) return "normal";
        if (ratio < 0.9) return "low";
        return "high";
    }
  }

  getBloodPressureStatus(systolic, diastolic) {
    if (systolic < 120 && diastolic < 80) return "normal";
    if (systolic < 130 && diastolic < 80) return "elevated";
    if (systolic < 140 || diastolic < 90) return "stage1";
    return "stage2";
  }

  getBMIStatus(bmi) {
    if (bmi < 18.5) return "underweight";
    if (bmi < 25) return "normal";
    if (bmi < 30) return "overweight";
    return "obese";
  }

  get getBiomonitoringData() {
    return this.biomonitoringData;
  }

  get getDietaryGoals() {
    return this.dietaryGoals;
  }

  get getWeeklyMeals() {
    return this.weeklyMeals;
  }

  get getBiomonitoringRequestState() {
    return this.biomonitoringRequestState;
  }

  get getDietaryGoalsRequestState() {
    return this.dietaryGoalsRequestState;
  }

  get getWeeklyMealsRequestState() {
    return this.weeklyMealsRequestState;
  }
}

export default DashboardController;
